import React, { useEffect, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/client'
import dayjs from 'dayjs'
import { Button, DatePicker, Form, Input, InputNumber, Result, Select, Space, Spin, message } from 'antd'
import { FilePdfOutlined } from '@ant-design/icons'

import Layout from '../App/layout'
import LayoutContentWrapper from '../../components/utility/layoutWrapper'
import ContentLayout from '../../components/utility/layoutContent'

import { AttachedFiles, SignedIdUploader } from './ContractFiles'
import { openContractPdf } from './pdf'
import {
  CREATE_EMPLOYEE_CONTRACT_MUTATION,
  DEPARTMENTS,
  EMPLOYEE_CONTRACT_QUERY,
  REMOVE_EMPLOYEE_CONTRACT_FILE_MUTATION,
  UPDATE_EMPLOYEE_CONTRACT_MUTATION,
  formatMutationErrors
} from './graphql'

const DATE_FORMAT = 'YYYY-MM-DD'
const DISPLAY_FORMAT = 'DD/MM/YYYY'

const toFormValues = contract => ({
  date: contract.date ? dayjs(contract.date) : null,
  department: contract.department || undefined,
  name: contract.name || '',
  position: contract.position || '',
  email: contract.email || '',
  idCardNumber: contract.idCardNumber || '',
  salary: contract.salary && contract.salary.value ? contract.salary.value : null
})

const presentIds = ids => (ids && ids.length ? ids : undefined)

const EmployeeContractFormContainer = () => {
  const { t } = useTranslation('employeeContracts')
  const history = useHistory()
  const { id } = useParams()
  const isEdit = Boolean(id)
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState({ signedContracts: false, documents: false })
  const [removingId, setRemovingId] = useState(null)
  const [pdfLoading, setPdfLoading] = useState(false)

  const { data, loading } = useQuery(EMPLOYEE_CONTRACT_QUERY, {
    skip: !isEdit,
    variables: { id },
    fetchPolicy: 'network-only'
  })

  const [createEmployeeContract] = useMutation(CREATE_EMPLOYEE_CONTRACT_MUTATION)
  const [updateEmployeeContract] = useMutation(UPDATE_EMPLOYEE_CONTRACT_MUTATION)
  const [removeEmployeeContractFile] = useMutation(REMOVE_EMPLOYEE_CONTRACT_FILE_MUTATION)

  const contract = data && data.employeeContract
  const isUploading = uploading.signedContracts || uploading.documents
  const pageTitle = isEdit ? t('edit') : t('new')

  useEffect(() => {
    document.title = 'Employee Contracts | OSH CRM'
  }, [])

  useEffect(() => {
    if (contract) form.setFieldsValue(toFormValues(contract))
  }, [contract && contract.id])

  const setUploadingFor = key => (value) => {
    setUploading(current => (current[key] === value ? current : { ...current, [key]: value }))
  }

  const buildVariables = (values) => {
    const variables = {
      date: values.date.format(DATE_FORMAT),
      department: values.department || null,
      name: values.name.trim(),
      position: values.position.trim(),
      email: values.email.trim(),
      idCardNumber: (values.idCardNumber || '').trim() || null,
      salary: values.salary,
      signedContractSignedIds: presentIds(values.signedContractSignedIds),
      documentSignedIds: presentIds(values.documentSignedIds)
    }

    return isEdit ? { ...variables, id } : variables
  }

  const handleSubmit = async (values) => {
    setSaving(true)
    try {
      const mutate = isEdit ? updateEmployeeContract : createEmployeeContract
      const { data: response } = await mutate({ variables: buildVariables(values) })
      const payload = response && (isEdit ? response.updateEmployeeContract : response.createEmployeeContract)
      if (!payload) { message.error(t('saveFailed')); return }
      if (payload.errors && payload.errors.length) { message.error(formatMutationErrors(payload.errors)); return }

      message.success(t('saveSuccess'))
      history.push('/employee_contracts')
    } catch (error) {
      message.error(error.message)
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveFile = async (file) => {
    setRemovingId(file.id)
    try {
      const { data: response } = await removeEmployeeContractFile({
        variables: { contractId: id, attachmentId: file.id }
      })
      const payload = response && response.removeEmployeeContractFile
      if (!payload) { message.error(t('removeFileFailed')); return }
      if (payload.errors && payload.errors.length) { message.error(formatMutationErrors(payload.errors)); return }

      message.success(t('fileRemoved'))
    } catch (error) {
      message.error(error.message)
    } finally {
      setRemovingId(null)
    }
  }

  const handleDownloadPdf = async () => {
    setPdfLoading(true)
    try {
      const opened = await openContractPdf(id)
      if (!opened) message.error(t('pdfFailed'))
    } catch (error) {
      message.error(error.message)
    } finally {
      setPdfLoading(false)
    }
  }

  const renderBody = () => {
    if (isEdit && loading) return <Spin />

    if (isEdit && !contract) {
      return (
        <Result
          extra={<Link to='/employee_contracts'>{t('backToList')}</Link>}
          status='404'
          title={t('notFound')}
        />
      )
    }

    return (
      <>
        {isEdit && (
          <Space style={{ marginBottom: 16 }} wrap>
            {contract.staff && (
              <span>{t('createdBy')}: <strong>{contract.staff.fullName}</strong></span>
            )}
            <Button icon={<FilePdfOutlined />} loading={pdfLoading} onClick={handleDownloadPdf} size='small'>
              {t('downloadPdf')}
            </Button>
          </Space>
        )}

        <Form
          disabled={saving}
          form={form}
          layout='vertical'
          onFinish={handleSubmit}
          style={{ maxWidth: 560 }}
        >
          <Form.Item
            label={t('date')}
            name='date'
            rules={[{ required: true, message: t('required') }]}
          >
            <DatePicker format={DISPLAY_FORMAT} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label={t('department')} name='department'>
            <Select allowClear placeholder={t('department')}>
              {DEPARTMENTS.map(value => (
                <Select.Option key={value} value={value}>{t(`departments.${value}`)}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('name')}
            name='name'
            rules={[{ required: true, whitespace: true, message: t('required') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('position')}
            name='position'
            rules={[{ required: true, whitespace: true, message: t('required') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('email')}
            name='email'
            rules={[
              { required: true, whitespace: true, message: t('required') },
              { type: 'email', message: t('invalidEmail') }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item label={t('idCardNumber')} name='idCardNumber'>
            <Input />
          </Form.Item>

          <Form.Item
            label={t('salary')}
            name='salary'
            rules={[
              { required: true, message: t('required') },
              { type: 'number', min: 0.01, message: t('salaryPositive') }
            ]}
          >
            <InputNumber
              prefix='฿'
              min={0}
              precision={2}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item label={t('signedContracts')}>
            {isEdit && (
              <AttachedFiles
                disabled={saving}
                files={contract.signedContracts}
                onRemove={handleRemoveFile}
                removingId={removingId}
              />
            )}
            <Form.Item name='signedContractSignedIds' noStyle>
              <SignedIdUploader onUploadingChange={setUploadingFor('signedContracts')} />
            </Form.Item>
          </Form.Item>

          <Form.Item label={t('documents')}>
            {isEdit && (
              <AttachedFiles
                disabled={saving}
                files={contract.documents}
                onRemove={handleRemoveFile}
                removingId={removingId}
              />
            )}
            <Form.Item name='documentSignedIds' noStyle>
              <SignedIdUploader onUploadingChange={setUploadingFor('documents')} />
            </Form.Item>
          </Form.Item>

          <Space>
            <Button disabled={isUploading} htmlType='submit' loading={saving} type='primary'>
              {t('save')}
            </Button>
            <Link to='/employee_contracts'>{t('backToList')}</Link>
          </Space>
        </Form>
      </>
    )
  }

  return (
    <Layout>
      <LayoutContentWrapper breadcrumbs={[
        { path: '/employee_contracts', name: t('title') },
        { name: pageTitle }
      ]}>
        <ContentLayout>
          <h2>{pageTitle}</h2>
          {renderBody()}
        </ContentLayout>
      </LayoutContentWrapper>
    </Layout>
  )
}

EmployeeContractFormContainer.propTypes = {}

EmployeeContractFormContainer.defaultProps = {}

export default EmployeeContractFormContainer
